import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, Settings as SettingsIcon, Landmark, Check, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUIStore } from '@/stores/ui-store'
import { useWorkspaceAccess } from '@/features/auth/workspaceAccess'
import { Permissions } from '@/lib/permissions'

/**
 * Workspace configuration
 */
const workspaces = [
  {
    id: 'operations' as const,
    label: 'Operations',
    icon: SettingsIcon,
    description: 'Quotations, Job Orders, Tasks',
    defaultRoute: '/',
    // operations:access — or an Admin (see workspaceAccess). It was `null`, "always available".
    requiredPermission: Permissions.OPERATIONS_ACCESS,
  },
  {
    id: 'finance' as const,
    label: 'Finance',
    icon: Landmark,
    description: 'Transactions, Reports, Accounting',
    defaultRoute: '/finance',
    requiredPermission: Permissions.FINANCE_ACCESS,
  },
  {
    id: 'sales' as const,
    label: 'Sales',
    icon: TrendingUp,
    description: 'Pipeline, customers, dashboard',
    defaultRoute: '/sales/home',
    requiredPermission: Permissions.SALES_ACCESS,
  },
]


/**
 * What the Sales row promises has to be what the reader will find there.
 *
 * "Pipeline, customers, dashboard" is three things an approver cannot open — every one of
 * those routes is `ModuleGate`d on `sales:access`. A menu item that names what is behind a
 * door the reader cannot walk through is the same defect as a nav entry that dead-ends; it
 * just fails one click later.
 */
function descriptionFor(id: string, hasSalesAccess: boolean, fallback: string): string {
  if (id === 'sales' && !hasSalesAccess) return 'Customer onboarding'
  return fallback
}

/**
 * WorkspaceSwitcher component
 * Allows users to switch between Operations and Finance workspaces
 * Permission-gated: Finance only visible to users with finance permissions
 */
export function WorkspaceSwitcher() {
  const navigate = useNavigate()
  const location = useLocation()
  const { workspace, setWorkspace } = useUIStore()
  const access = useWorkspaceAccess()
  const hasSalesAccess = access.salesSeat

  // Filter workspaces based on permissions. Per RBAC Module spec #10 §3.7 +
  // §5.4 — we will also AND-compose with tenant module enablement once the
  // useTenantModules hook is wired (FE Phase 14/17). For now, permission alone.
  const availableWorkspaces = workspaces.filter((w) => access[w.id])

  // Get current workspace config
  const currentWorkspace = workspaces.find(w => w.id === workspace) || workspaces[0]
  const CurrentIcon = currentWorkspace.icon

  // Handle workspace change
  const handleWorkspaceChange = (newWorkspace: typeof workspace) => {
    if (newWorkspace === workspace) return

    setWorkspace(newWorkspace)

    // Navigate to the default route for the new workspace
    const targetWorkspace = workspaces.find(w => w.id === newWorkspace)
    if (targetWorkspace) {
      // Detect which workspace the user is currently inside · navigate only if changing
      const isInFinance = location.pathname.startsWith('/finance')
      const isInSales = location.pathname.startsWith('/sales')
      const switchingToFinance = newWorkspace === 'finance'
      const switchingToSales = newWorkspace === 'sales'

      const currentlyInTargetWorkspace =
        (switchingToFinance && isInFinance) ||
        (switchingToSales && isInSales) ||
        (newWorkspace === 'operations' && !isInFinance && !isInSales)

      if (!currentlyInTargetWorkspace) {
        // An approver without a Sales seat lands on Onboarding rather than on `/sales/home`,
        // which would `ModuleGate` them straight into Unauthorized — a switcher entry that
        // dead-ends is the same defect as a nav item that dead-ends.
        const landing = newWorkspace === 'sales' && !hasSalesAccess
          ? '/sales/onboarding'
          : targetWorkspace.defaultRoute
        navigate(landing)
      }
    }
  }

  // If only one workspace available, don't show switcher
  if (availableWorkspaces.length <= 1) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'h-9 gap-2 px-3 font-medium',
            'hover:bg-muted/50',
            'border border-border/50 rounded-lg'
          )}
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="hidden sm:inline">{currentWorkspace.label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          Switch workspace
        </div>
        <DropdownMenuSeparator />
        {availableWorkspaces.map((ws) => {
          const Icon = ws.icon
          const isActive = workspace === ws.id
          return (
            <DropdownMenuItem
              key={ws.id}
              onClick={() => handleWorkspaceChange(ws.id)}
              className={cn(
                'flex items-start gap-3 p-3 cursor-pointer',
                isActive && 'bg-primary/5'
              )}
            >
              <div className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                isActive ? 'bg-primary' : 'bg-muted'
              )}>
                <Icon className={cn('h-4 w-4', isActive && 'text-white')} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{ws.label}</span>
                  {isActive && <Check className="h-3.5 w-3.5 text-primary" />}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {descriptionFor(ws.id, hasSalesAccess, ws.description)}
                </p>
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
